/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PbItemComponentFinderView } from '@gh/builder-shared-data';
import {
  DecoratingReadOnlySelectionModel,
  EnumDictionary,
  FormState,
  FormStateBuilder,
  getDialogInitData,
  MutableSelectionModel,
  newSelectionModel,
  toAppFormControl,
} from '@gh/common-shared';
import { DialogInput, getControl, getControlValue, setControlValue } from '@gh/core-ui';
import { coalesce, isNotNil } from '@gh/core-util';
import { PbComponentItemFinderDialogConfig } from '../../resources/pb-component-item-finder-business.service';
import {
  getPbComponentItemFinderListItemId,
  PbComponentItemFinderListItem,
} from '../../resources/pb-component-item-finder.model';

@Component({
  selector: 'gh-pb-component-item-finder-dialog',
  template: require('./pb-component-item-finder-dialog.component.html'),
  styles: [require('./pb-component-item-finder-dialog.component.scss')],
})
export class PbComponentItemFinderDialogComponent implements OnInit, DialogInput<PbComponentItemFinderDialogConfig> {
  views: EnumDictionary<PbItemComponentFinderView>;
  input: PbComponentItemFinderDialogConfig;

  form: FormState<any>;
  selectionModel: MutableSelectionModel<PbComponentItemFinderListItem>;

  currentName: string;

  activeView: PbItemComponentFinderView;

  private visibleViews: Hash<boolean> = {};

  constructor(private formStateBuilder: FormStateBuilder,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private dialogRef: MatDialogRef<any>) {
  }

  get priceBookName(): string {
    return this.input.priceBookName;
  }

  get housePlanName(): Maybe<string> {
    return this.input.housePlanName;
  }

  get jobNumber(): Maybe<string> {
    return this.input.jobNumber;
  }

  get hasSelected(): boolean {
    return !!this.selectionModel && this.selectionModel.hasChanges();
  }

  get isForHousePlan(): boolean {
    return isNotNil(this.input.housePlanId);
  }

  get isForJob(): boolean {
    return isNotNil(this.input.jobId);
  }

  get multiselection(): boolean {
    return coalesce(this.input.multiselection, false);
  }

  get selectionSize(): number {
    return this.selectionModel.getTotalCount();
  }

  ngOnInit(): void {
    const { views } = getDialogInitData(this.dialogData);

    this.views = views;
    this.form = this.formStateBuilder.buildPlain({
      value: { name: '' },
      children: { name: { enabled: false } },
    });

    const baseModel = this.input?.selectionModel || newSelectionModel(this.input?.preselected || []);
    this.selectionModel = new MutableSelectionModel(baseModel, getPbComponentItemFinderListItemId);

    this.selectView(PbItemComponentFinderView.FullList);
  }

  onSearch(): void {
    this.currentName = getControlValue(this.form.group, 'name');
  }

  onViewChanged(view: PbItemComponentFinderView): void {
    this.selectView(view);
  }

  onApply(): void {
    this.dialogRef.close(new DecoratingReadOnlySelectionModel(this.selectionModel));
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  isViewVisible(view: PbItemComponentFinderView): boolean {
    return this.visibleViews[view] || false;
  }

  isViewActive(view: PbItemComponentFinderView): boolean {
    return this.activeView === view;
  }

  private selectView(view: PbItemComponentFinderView): void {
    this.visibleViews[view] = true;
    this.activeView = view;

    if (view === PbItemComponentFinderView.FullList) {
      this.onSearch();
    } else {
      setControlValue(this.form.group, 'name', '');
    }

    toAppFormControl(getControl(this.form.group, 'name'))
      .updateSchema({ enabled: view === PbItemComponentFinderView.FullList });
  }
}
